import { useEffect, useRef, useState } from "react";
import { useReducedMotion } from "framer-motion";
import {
  BookOpen,
  Bot,
  BrainCircuit,
  Compass,
  FileText,
  FolderOpen,
  Layers,
  Maximize2,
  Minus,
  Plus,
  Sparkles,
  Users,
} from "lucide-react";
import { ROOT, neighborhood, zoomAt } from "./atlasModel";
const icons = {
  agent: Bot,
  memory: BrainCircuit,
  preferences: Users,
  skills: Layers,
  learnings: Sparkles,
  brand: FolderOpen,
  shared: BookOpen,
  operating: Layers,
  playbook: BookOpen,
  domain: Compass,
  group: FolderOpen,
};
export default function AtlasMap({
  nodes,
  edges,
  focusId,
  onFocus,
  onOpen,
  located,
  moving = true,
}) {
  const host = useRef(null),
    cameraRef = useRef({ x: 0, y: 0, k: 0.6 });
  const [size, setSize] = useState({ width: 1100, height: 650 });
  const [camera, setCamera] = useState(cameraRef.current),
    [dragging, setDragging] = useState(false);
  const pointers = useRef(new Map()),
    gesture = useRef(null),
    history = useRef(new Map()),
    previous = useRef(null),
    moved = useRef(false);
  const reduced = useReducedMotion();
  const dimensions = useRef(null);
  const { focus, children, visible } = neighborhood(nodes, focusId);
  const setView = (value) => {
    cameraRef.current = value;
    setCamera(value);
  };
  const fitted = () => {
    const k = Math.max(
      size.width < 600 ? 0.38 : 0.2,
      focus.id === ROOT
        ? Math.min(size.width / 2050, size.height / 1440)
        : Math.min(
            size.width /
              (children.length > 8 ? 2050 : focus.depth === 1 ? 1520 : 1220),
            size.height /
              (children.length > 8 ? 1120 : focus.depth === 1 ? 990 : 890),
          ),
    );
    return {
      x: size.width / 2 - focus.x * k,
      y: size.height / 2 - focus.y * k,
      k,
    };
  };
  useEffect(() => {
    const obs = new ResizeObserver(([e]) => {
      if (e.contentRect.width)
        setSize({ width: e.contentRect.width, height: e.contentRect.height });
    });
    if (host.current) obs.observe(host.current);
    return () => obs.disconnect();
  }, []);
  useEffect(() => {
    const resized = dimensions.current !== `${size.width}:${size.height}`;
    if (previous.current && previous.current !== focus.id)
      history.current.set(previous.current, cameraRef.current);
    previous.current = focus.id;
    dimensions.current = `${size.width}:${size.height}`;
    setView(
      !resized && history.current.has(focus.id)
        ? history.current.get(focus.id)
        : fitted(),
    );
    // Coordinates remain stable; data refresh must not reset manual camera.
  }, [focus.id, size.width, size.height]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    const el = host.current;
    const wheel = (e) => {
      e.preventDefault();
      setDragging(true);
      const rect = el.getBoundingClientRect();
      setView(
        zoomAt(
          cameraRef.current,
          Math.exp(-e.deltaY * (e.ctrlKey ? 0.009 : 0.002)),
          { x: e.clientX - rect.left, y: e.clientY - rect.top },
        ),
      );
    };
    el.addEventListener("wheel", wheel, { passive: false });
    return () => el.removeEventListener("wheel", wheel);
  }, []);
  const pointer = (e) => ({
    x: e.clientX - host.current.getBoundingClientRect().left,
    y: e.clientY - host.current.getBoundingClientRect().top,
  });
  const start = (e) => {
    if (e.target.closest("button")) return;
    host.current.setPointerCapture?.(e.pointerId);
    pointers.current.set(e.pointerId, pointer(e));
    const points = [...pointers.current.values()];
    gesture.current = {
      camera: cameraRef.current,
      points,
      distance:
        points.length > 1
          ? Math.hypot(points[0].x - points[1].x, points[0].y - points[1].y)
          : 0,
    };
    moved.current = false;
    setDragging(true);
  };
  const move = (e) => {
    if (!pointers.current.has(e.pointerId) || !gesture.current) return;
    pointers.current.set(e.pointerId, pointer(e));
    const p = [...pointers.current.values()],
      g = gesture.current;
    if (p.length === 2 && g.points.length === 2 && g.distance) {
      const old = {
        x: (g.points[0].x + g.points[1].x) / 2,
        y: (g.points[0].y + g.points[1].y) / 2,
      };
      const next = zoomAt(
        g.camera,
        Math.hypot(p[0].x - p[1].x, p[0].y - p[1].y) / g.distance,
        old,
      );
      setView({
        ...next,
        x: next.x + (p[0].x + p[1].x) / 2 - old.x,
        y: next.y + (p[0].y + p[1].y) / 2 - old.y,
      });
    } else if (p.length === 1)
      setView({
        ...g.camera,
        x: g.camera.x + p[0].x - g.points[0].x,
        y: g.camera.y + p[0].y - g.points[0].y,
      });
    moved.current = true;
  };
  const end = (e) => {
    pointers.current.delete(e.pointerId);
    gesture.current = null;
    setDragging(false);
  };
  const zoom = (factor) => {
    setDragging(false);
    setView(
      zoomAt(cameraRef.current, factor, {
        x: size.width / 2,
        y: size.height / 2,
      }),
    );
  };
  const open = (n) => {
    setDragging(false);
    onFocus(n.id === focus.id && n.parent ? n.parent : n.id);
    if (
      n.nodeType === "entry" ||
      ["playbook", "catalog", "playbookSection"].includes(n.kind)
    )
      onOpen(n);
  };
  const focused = focus.id !== ROOT;
  return (
    <div
      ref={host}
      className={`atlas-canvas ${dragging ? "is-dragging" : ""} ${!moving || reduced ? "no-motion" : ""}`}
      data-testid="brain-constellation"
      tabIndex={0}
      role="region"
      aria-label="Interactive knowledge map. Use plus and minus to zoom, arrow keys to pan."
      onKeyDown={(e) => {
        if (e.target !== e.currentTarget) return;
        if (
          [
            "+",
            "=",
            "-",
            "ArrowLeft",
            "ArrowRight",
            "ArrowUp",
            "ArrowDown",
          ].includes(e.key)
        ) {
          e.preventDefault();
          setDragging(false);
          if (e.key === "+" || e.key === "=") zoom(1.25);
          else if (e.key === "-") zoom(0.8);
          else
            setView({
              ...cameraRef.current,
              x:
                cameraRef.current.x +
                (e.key === "ArrowLeft" ? 60 : e.key === "ArrowRight" ? -60 : 0),
              y:
                cameraRef.current.y +
                (e.key === "ArrowUp" ? 60 : e.key === "ArrowDown" ? -60 : 0),
            });
        }
      }}
      onPointerDown={start}
      onPointerMove={move}
      onPointerUp={end}
      onPointerCancel={end}
    >
      <div className="atlas-map-grain" aria-hidden="true" />
      <svg className="atlas-web" width="100%" height="100%" aria-hidden="true">
        <g
          style={{
            transform: `translate(${camera.x}px,${camera.y}px) scale(${camera.k})`,
          }}
        >
          {edges.map((edge, i) => {
            const a = nodes.find((n) => n.id === edge.from),
              b = nodes.find((n) => n.id === edge.to);
            if (!a || !b) return null;
            const active = visible.has(a.id) && visible.has(b.id);
            return (
              <path
                key={i}
                d={`M${a.x},${a.y} Q${a.x + (b.x - a.x) * 0.6},${a.y} ${b.x},${b.y}`}
                stroke={b.color}
                strokeWidth={active ? 1.3 / camera.k : 0.65 / camera.k}
                opacity={active ? 0.42 : focused ? 0.065 : 0.12}
                fill="none"
                strokeDasharray={
                  edge.relation === "supported by"
                    ? `${4 / camera.k} ${5 / camera.k}`
                    : undefined
                }
              />
            );
          })}
        </g>
      </svg>
      {nodes.map((n) => {
        const x = n.x * camera.k + camera.x,
          y = n.y * camera.k + camera.y;
        if (
          x < -180 ||
          x > size.width + 180 ||
          y < -130 ||
          y > size.height + 130
        )
          return null;
        const primary = n.id === focus.id,
          related = visible.has(n.id),
          expanded =
            primary || n.parent === focus.id || (camera.k > 1.4 && related);
        const Icon = icons[n.id] || icons[n.kind] || FileText;
        const branch =
          !n.nodeType &&
          !["playbook", "catalog", "playbookSection"].includes(n.kind);
        return (
          <button
            key={n.id}
            type="button"
            className={`atlas-node ${primary ? "atlas-focus" : ""} ${expanded ? "atlas-expanded" : "atlas-distant"} ${n.kind === "playbook" ? "atlas-playbook-node" : ""} ${located === n.id ? "solar-located" : ""}`}
            style={{
              transform: `translate(${x}px, ${y}px) translate(-50%, -50%)`,
              "--node-color": n.color,
              opacity: expanded ? 1 : related ? 0.6 : 0.24,
            }}
            onClick={() => open(n)}
            aria-label={
              branch
                ? primary && n.id !== ROOT
                  ? `Back from ${n.title}`
                  : `Explore ${n.title}`
                : `Open ${n.title}`
            }
            aria-hidden={!expanded}
            tabIndex={expanded ? 0 : -1}
          >
            <span className="atlas-node-glyph">
              <Icon
                size={primary ? 32 : n.kind === "playbook" ? 23 : 22}
                strokeWidth={1.6}
              />
              {n.id === "shared" && n.connection?.accessGranted && (
                <i className="atlas-connection-dot" />
              )}
            </span>
            {expanded && (
              <>
                <strong>{n.title}</strong>
                <small>
                  {primary && n.id === ROOT
                    ? "YOUR HERMES AGENT"
                    : n.kind === "playbook"
                      ? "PLAYBOOK · EXPLORE METHOD"
                      : n.kind === "domain"
                        ? `${n.count.toLocaleString()} source documents`
                        : n.kind === "collection" || n.kind === "group"
                          ? `${n.count} ${n.id === "skills" ? "skills" : "sources"}`
                          : n.sourceLabel}
                </small>
                {located === n.id && <em>Located here</em>}
              </>
            )}
          </button>
        );
      })}
      <div className="atlas-canvas-help">
        <Compass size={13} />
        <span>Scroll to zoom · Drag to explore · Select to focus</span>
      </div>
      <div className="atlas-camera-tools" aria-label="Map navigation">
        <button onClick={() => zoom(0.8)} aria-label="Zoom out">
          <Minus size={16} />
        </button>
        <output aria-live="polite">{Math.round(camera.k * 100)}%</output>
        <button onClick={() => zoom(1.25)} aria-label="Zoom in">
          <Plus size={16} />
        </button>
        <span />
        <button
          onClick={() => {
            setDragging(false);
            setView(fitted());
          }}
          aria-label="Fit neighborhood"
        >
          <Maximize2 size={16} />
        </button>
      </div>
      <svg
        className="atlas-minimap"
        viewBox="-1200 -1000 2400 2000"
        aria-label="Map overview"
        role="img"
      >
        {nodes
          .filter((n) => n.depth < 2)
          .map((n) => (
            <circle
              key={n.id}
              cx={n.x}
              cy={n.y}
              r={n.id === focus.id ? 65 : 35}
              fill={n.color}
              opacity={n.id === focus.id ? 1 : 0.5}
            />
          ))}
        <rect
          x={-camera.x / camera.k}
          y={-camera.y / camera.k}
          width={size.width / camera.k}
          height={size.height / camera.k}
          rx="40"
          fill="none"
          stroke="#557493"
          strokeWidth="12"
        />
      </svg>
    </div>
  );
}
